/** Table primitives that keep the fading row rules intact.
 *
 * Nocturne paints a table's rules on the row, not the cell, so the fade runs
 * the width of the row instead of restarting at each border. That only works
 * if the table itself never gets `overflow`, which is why horizontal scroll
 * belongs to a wrapper — `<Table scroll>` — and not to the element. The lap
 * table is wide enough to need it on every screen narrower than a laptop.
 *
 * `numeric` on a cell is not decoration: every figure in this portal is read
 * down a column, so a numeric cell is right-aligned, tabular and non-wrapping
 * in one word rather than three utilities repeated forty times.
 */

import type { KeyboardEvent, ReactNode, CSSProperties } from 'react';

export interface TableProps {
  children: ReactNode;
  /** Wraps the table so it can scroll sideways without breaking the rules. */
  scroll?: boolean;
  className?: string;
  /** Names the table for a screen reader when no caption is visible. */
  label?: string;
  style?: CSSProperties;
}

export function Table({ children, scroll, className, label, style }: TableProps) {
  const table = (
    <table
      className={['table', className ?? ''].filter(Boolean).join(' ')}
      aria-label={label}
      style={style}
    >
      {children}
    </table>
  );
  return scroll ? <div className="table-scroll">{table}</div> : table;
}

export function THead({ children }: { children: ReactNode }) {
  return <thead>{children}</thead>;
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody>{children}</tbody>;
}

export interface TRProps {
  children: ReactNode;
  /** Draws the accent bar and the 8% wash the design gives a chosen row. */
  selected?: boolean;
  /** Makes the whole row a control, as the lap table and the issue inbox
   *  want it. The row becomes focusable and answers Enter and Space. */
  onSelect?: () => void;
  className?: string;
  style?: CSSProperties;
}

export function TR({ children, selected, onSelect, className, style }: TRProps) {
  if (!onSelect) {
    return (
      <tr data-selected={selected ? 'true' : undefined} className={className} style={style}>
        {children}
      </tr>
    );
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <tr
      tabIndex={0}
      aria-selected={selected}
      data-selected={selected ? 'true' : undefined}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      className={className}
      style={{ cursor: 'pointer', ...style }}
    >
      {children}
    </tr>
  );
}

export interface CellProps {
  children?: ReactNode;
  /** Right-aligned, tabular, non-wrapping. */
  numeric?: boolean;
  align?: 'left' | 'right' | 'center';
  mono?: boolean;
  muted?: boolean;
  nowrap?: boolean;
  /** Any colour token — a sector best in the accent, a queued lap in warn. */
  color?: string;
  colSpan?: number;
  /** `col` or `row`, when the cell heads one. */
  scope?: 'col' | 'row';
  className?: string;
  style?: CSSProperties;
  title?: string;
}

function cellStyle({ numeric, align, mono, muted, nowrap, color, style }: CellProps): CSSProperties {
  return {
    textAlign: align ?? (numeric ? 'right' : undefined),
    fontVariantNumeric: numeric ? 'tabular-nums' : undefined,
    whiteSpace: nowrap || numeric ? 'nowrap' : undefined,
    fontFamily: mono ? 'var(--font-mono)' : undefined,
    fontSize: mono ? '12.5px' : undefined,
    color: color ?? (muted ? 'var(--color-neutral-400)' : undefined),
    ...style,
  };
}

export function TH(props: CellProps) {
  const { children, colSpan, scope = 'col', className, title } = props;
  return (
    <th scope={scope} colSpan={colSpan} className={className} style={cellStyle(props)} title={title}>
      {children}
    </th>
  );
}

export function TD(props: CellProps) {
  const { children, colSpan, className, title } = props;
  return (
    <td colSpan={colSpan} className={className} style={cellStyle(props)} title={title}>
      {children}
    </td>
  );
}

export default Table;
